import { Router, ScreenBase, watchBusy, BusyWatcher } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import { attachAutomaticDirtyWatcher, isDirty, resetDirty } from "@frui.ts/dirtycheck";
import * as Sentry from "@sentry/react";
import { action, computed, observable } from "mobx";
import PolicyRepository from "repositories/policyRepository";
import LocalizationService from "services/localizationService";
import Policy, { PasswordPolicyValidationRules } from "entities/policy";
import NotificationService, { NotificationType } from "services/notificationService";
import SecurityService from "services/securityService";
import { Modules } from "config";

const navigationName = "policies";

@Router.registerRoute({ route: navigationName, name: Router.Self })
export default class PoliciesViewModel extends ScreenBase {
  @observable passwordPolicy: Policy;
  @observable busyWatcher: BusyWatcher = new BusyWatcher();

  @computed
  get isDirty() {
    return isDirty(this.passwordPolicy);
  }

  constructor(
    private repository: PolicyRepository,
    private localizationService: LocalizationService,
    private notificationService: NotificationService,
    private securityService: SecurityService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get isEditable() {
    return this.securityService.allowTo("users", "write");
  }

  @watchBusy
  protected async onInitialize() {
    await this.initializePasswordPolicy();

    return super.onInitialize();
  }

  protected async initializePasswordPolicy() {
    const passwordPolicy = await this.repository.passwordPolicy();
    attachAutomaticValidator(passwordPolicy, PasswordPolicyValidationRules);
    attachAutomaticDirtyWatcher(passwordPolicy);
    this.passwordPolicy = passwordPolicy;
  }

  @action.bound
  @watchBusy
  async onSavePasswordPolicy() {
    if (validate(this.passwordPolicy)) {
      try {
        await this.repository.updatePasswordPolicy(this.passwordPolicy);
        this.notificationService.add({ title: this.translate("passwordPolicy.updated"), type: NotificationType.SUCCESS });
        resetDirty(this.passwordPolicy);
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }

  @action.bound
  @watchBusy
  async onResetClick() {
    await this.initializePasswordPolicy();
  }

  translateAttribute(attribute: keyof Policy) {
    return this.localizationService.translateAttribute("policy", attribute);
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.users, `policies.${code}`, size);
  }
}
